import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { JSX, MouseEvent, ReactNode } from "react";
import { MarionPlayer, type MarionRenderMode } from "./MarionPlayer";
import type { ChoicePreview } from "./ChoiceList";

export type VisualNovelMarionActor = {
  id: string;
  characterId?: string | undefined;
  isSpeaking?: boolean | undefined;
  name: string;
  manifestUrl?: string | null;
  fallbackImageUrl?: string | null;
  fallbackImageBackupUrl?: string | null;
  exactImageOnly?: boolean | undefined;
  variantId?: string | null | undefined;
  shot?: "standing" | "cutin" | "portrait" | undefined;
  renderMode?: MarionRenderMode | undefined;
  position: "left" | "center" | "right";
};

export type VisualNovelActorCue = {
  speakerId: string;
  actingVariantId?: string | undefined;
  cutin?: true | undefined;
  cutinImageUrl?: string | undefined;
};

export type VisualNovelCutinActor = {
  id: string;
  characterId?: string | undefined;
  name: string;
  manifestUrl?: string | null;
  imageUrl: string;
  fallbackImageBackupUrl?: string | null;
  variantId?: string | null | undefined;
  exactImageOnly?: boolean | undefined;
  position: "left" | "right";
};

type VisualNovelLayoutProps = {
  children: ReactNode;
  /** 同一シーン内の立ち絵登場演出を初回だけにするための scene identity。 */
  sceneId?: string | undefined;
  /** シーンに対応する背景画像 URL。未指定または読み込み失敗時はグラデーション placeholder を表示する */
  // exactOptionalPropertyTypes 下で `imageUrl={string | undefined}` を直接渡せるよう | undefined を明示する
  imageUrl?: string | undefined;
  /** 背景画像エリア上に重ねる現在話者の Marion 表示。null/undefined なら非表示 */
  marionActor?: VisualNovelMarionActor | null | undefined;
  /** 背景画像エリア上に重ねる複数の Marion 立ち絵。指定時は marionActor より優先する。 */
  marionActors?: VisualNovelMarionActor[] | null | undefined;
  /** 選択肢や演出で使う一枚絵カットイン。立ち絵とは別レイヤーで表示する。 */
  cutinActor?: VisualNovelCutinActor | null | undefined;
  /** visual_novel の画面内右上に重ねる HUD 操作 */
  hud?: ReactNode | undefined;
  /** visual_novel の選択肢 1tap preview を背景・キャラの上に重ねる。 */
  choicePreview?: ChoicePreview | null | undefined;
  /** 選択肢表示中は、テキスト領域をステージ横の選択肢パネルとして配置する。 */
  choicePanelPosition?: "left" | "right" | undefined;
  /** 選択確定後、次シーン生成中の状態を選択肢 preview と同じステージ上に重ねる。 */
  isComposing?: boolean | undefined;
  composingPreview?: ChoicePreview | null | undefined;
  /** visual_novel mode の click/tap で次の文章を表示する。 */
  onAdvanceText?: () => void;
  /** 外側の余白と空の image stage に使う scenario screen palette。 */
  screenBackgroundColor?: string | null | undefined;
};

const ADVANCE_IGNORED_SELECTOR =
  "button, a, input, textarea, select, [role='button'], [role='dialog'], [data-visual-novel-ignore-advance]";

/**
 * visual_novel displayMode のレイアウト。
 * 上部：画像エリア（imageUrl 指定時は背景画像、未指定または読み込み失敗時はグラデーション）
 * 下部：固定高さのテキスト・選択肢エリア
 */
export function VisualNovelLayout({
  children,
  sceneId,
  imageUrl,
  marionActor,
  marionActors,
  cutinActor,
  hud,
  choicePreview,
  choicePanelPosition,
  isComposing = false,
  composingPreview,
  onAdvanceText,
  screenBackgroundColor,
}: VisualNovelLayoutProps): JSX.Element {
  // onError 発火時に true になり、placeholder フォールバックへ切り替える（req 5.2）
  const [hasError, setHasError] = useState(false);
  const [failedCutinUrl, setFailedCutinUrl] = useState<string | null>(null);
  const seenMarionActorKeysRef = useRef<Set<string>>(new Set());
  const [enteringMarionActorKeys, setEnteringMarionActorKeys] = useState<
    ReadonlySet<string>
  >(() => new Set());

  useEffect(() => {
    setHasError(false);
  }, [imageUrl]);

  useEffect(() => {
    setFailedCutinUrl(null);
  }, [cutinActor?.imageUrl]);

  const handleClick = useCallback((event: MouseEvent<HTMLDivElement>): void => {
    if (onAdvanceText === undefined) return;

    const target = event.target;
    if (
      target instanceof Element &&
      target.closest(ADVANCE_IGNORED_SELECTOR) !== null
    ) {
      return;
    }

    onAdvanceText();
  }, [onAdvanceText]);

  const showImage = Boolean(imageUrl) && !hasError;
  const cutinVisible = cutinActor != null && failedCutinUrl !== cutinActor.imageUrl;
  const marionLayerBaseClass =
    "pointer-events-none absolute inset-x-0 bottom-0 top-0 z-10";
  const resolvedMarionActors =
    marionActors !== undefined && marionActors !== null
      ? marionActors
      : marionActor !== null && marionActor !== undefined
        ? [marionActor]
        : [];
  const marionActorEntryKeySignature = JSON.stringify(
    resolvedMarionActors.map(getMarionActorEntryKey),
  );
  const marionActorEntryKeys = useMemo(() => {
    const parsed: unknown = JSON.parse(marionActorEntryKeySignature);
    return Array.isArray(parsed) && parsed.every((key) => typeof key === "string")
      ? parsed
      : [];
  }, [marionActorEntryKeySignature]);
  const hasSpeakingMarionActor = resolvedMarionActors.some(
    (actor) => actor.isSpeaking === true,
  );

  useEffect(() => {
    seenMarionActorKeysRef.current = new Set();
    setEnteringMarionActorKeys(new Set());
  }, [sceneId]);

  useEffect(() => {
    const newEntryKeys: string[] = [];
    for (const actorKey of marionActorEntryKeys) {
      if (seenMarionActorKeysRef.current.has(actorKey)) continue;
      seenMarionActorKeysRef.current.add(actorKey);
      newEntryKeys.push(actorKey);
    }
    if (newEntryKeys.length === 0) return undefined;

    setEnteringMarionActorKeys((prev) => {
      const next = new Set(prev);
      for (const actorKey of newEntryKeys) {
        next.add(actorKey);
      }
      return next;
    });

    const timeoutId = window.setTimeout(() => {
      setEnteringMarionActorKeys((prev) => {
        const next = new Set(prev);
        for (const actorKey of newEntryKeys) {
          next.delete(actorKey);
        }
        return next;
      });
    }, 260);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [marionActorEntryKeys, sceneId]);
  const cutinLayerBaseClass =
    "pointer-events-none absolute inset-0 z-20 flex items-center justify-center px-6 py-8 landscape:px-[9%] md:px-[8%]";
  const cutinLayerClass =
    cutinActor?.position === "left"
      ? `${cutinLayerBaseClass} translate-x-3 landscape:translate-x-4 md:translate-x-6 landscape:justify-start md:justify-start`
      : `${cutinLayerBaseClass} -translate-x-3 landscape:-translate-x-4 md:-translate-x-6 landscape:justify-end md:justify-end`;
  const choiceMonologue =
    choicePreview?.emotion !== undefined && choicePreview.emotion.trim().length > 0
      ? choicePreview.emotion
      : null;
  const shouldRenderChoiceMonologueDock =
    choicePanelPosition !== undefined && choiceMonologue !== null;
  const shouldRenderChoiceMonologueOverlay =
    choicePanelPosition === undefined && choiceMonologue !== null;
  const composingMonologue =
    composingPreview?.emotion !== undefined && composingPreview.emotion.trim().length > 0
      ? composingPreview.emotion
      : null;
  const composingStatusRail = isComposing ? (
    <div
      data-testid="visual-novel-composing-overlay"
      className="pointer-events-none absolute inset-x-4 bottom-4 z-[28] flex flex-col items-center justify-center text-center text-slate-50 max-md:landscape:bottom-3 landscape:bottom-5 md:bottom-6"
      role="status"
      aria-live="polite"
    >
      <div
        data-testid="visual-novel-composing-status-shell"
        className="relative inline-flex min-h-11 max-w-full items-center gap-3 overflow-hidden rounded-full bg-slate-950/82 px-5 py-2.5 text-slate-50 shadow-[0_0_24px_rgba(226,232,240,0.16),0_12px_34px_rgba(0,0,0,0.48)] ring-1 ring-white/22 backdrop-blur-md"
      >
        <span
          className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-slate-50/28 to-transparent motion-reduce:hidden animate-[composingProgressSweep_1.05s_linear_infinite]"
          aria-hidden="true"
        />
        <span className="relative flex size-3.5 shrink-0" aria-hidden="true">
          <span className="absolute inline-flex size-full rounded-full bg-slate-100/45 opacity-75 animate-ping" />
          <span className="relative inline-flex size-3.5 rounded-full bg-slate-50 shadow-[0_0_16px_rgba(248,250,252,0.65)]" />
        </span>
        <span
          data-testid="visual-novel-composing-status"
          className="relative text-base font-semibold leading-none tracking-normal text-slate-50 drop-shadow-[0_2px_8px_rgba(0,0,0,0.82)] md:text-lg"
        >
          物語を紡いでいます
        </span>
        <span className="relative flex items-end gap-1 motion-reduce:hidden" aria-hidden="true">
          <span className="size-1.5 rounded-full bg-slate-100/92 animate-bounce [animation-delay:-0.24s]" />
          <span className="size-1.5 rounded-full bg-slate-100/92 animate-bounce [animation-delay:-0.12s]" />
          <span className="size-1.5 rounded-full bg-slate-100/92 animate-bounce" />
        </span>
      </div>
      <div
        data-testid="visual-novel-composing-progress"
        className="relative mt-2.5 h-1 w-56 max-w-[68vw] overflow-hidden rounded-full bg-slate-100/18 shadow-[0_0_14px_rgba(226,232,240,0.16)] md:w-72"
        aria-hidden="true"
      >
        <span className="absolute inset-y-0 left-0 w-1/2 rounded-full bg-gradient-to-r from-transparent via-slate-50 to-transparent motion-reduce:hidden motion-reduce:animate-none animate-[composingProgressSweep_0.95s_linear_infinite]" />
        <span className="absolute inset-y-0 left-0 hidden w-full rounded-full bg-slate-100/42 motion-reduce:block" />
      </div>
    </div>
  ) : null;
  const textAreaBaseClass =
    "absolute bottom-4 inset-x-4 z-20 mx-auto h-36 max-h-36 min-h-36 w-auto max-w-[1200px] overflow-visible bg-slate-950/86 px-4 py-0 text-slate-50 landscape:bottom-3 landscape:h-32 landscape:max-h-32 landscape:min-h-32 md:bottom-6 md:inset-x-8 md:h-[9.5rem] md:max-h-[9.5rem] md:min-h-[9.5rem] md:px-6";
  const choicePanelBaseClass =
    "absolute top-[36%] z-[27] max-h-[calc(44%_-_1rem)] w-[min(64vw,27rem)] max-w-[calc(100vw-2rem)] overflow-y-auto overscroll-contain px-0 py-0 text-slate-50 landscape:w-[min(52vw,37rem)] md:w-[min(41vw,37rem)]";
  const choicePanelRightClass =
    "right-4 landscape:right-auto landscape:left-[calc(8%_+_min(38vw,32rem)_-_1.25rem)] md:right-auto md:left-[calc(8%_+_min(34vw,34rem)_-_1.25rem)]";
  const choicePanelLeftClass =
    "left-4 landscape:left-auto landscape:right-[calc(8%_+_min(38vw,32rem)_-_1.25rem)] md:left-auto md:right-[calc(8%_+_min(34vw,34rem)_-_1.25rem)]";
  const textAreaClass =
    choicePanelPosition === undefined
      ? textAreaBaseClass
      : `${choicePanelBaseClass} ${choicePanelPosition === "left" ? choicePanelLeftClass : choicePanelRightClass}`;
  const textArea = (
    <div
      data-testid="visual-novel-text-area"
      data-choice-panel-position={choicePanelPosition}
      className={textAreaClass}
      onClick={(event) => { event.stopPropagation(); }}
    >
      {children}
    </div>
  );

  return (
    <div
      data-testid="visual-novel-layout"
      className="relative mx-auto w-full max-w-[1600px] overflow-hidden rounded-lg bg-slate-950 shadow-[0_24px_80px_rgba(15,23,42,0.22)]"
      style={screenBackgroundColor ? { backgroundColor: screenBackgroundColor } : undefined}
      onClick={handleClick}
    >
      {/* 画像エリア。VNでは背景ステージを主役にし、会話UIは下部に固定する。 */}
      <div
        data-testid="visual-novel-image-area"
        className="relative flex aspect-[16/9] min-h-[22rem] max-h-[min(52rem,calc(100dvh-9.5rem))] w-full shrink-0 items-end overflow-hidden bg-gradient-to-b from-slate-950 via-reader-surface to-reader-page px-4 pb-4 max-md:landscape:min-h-[18rem] md:min-h-[24rem] md:max-h-[min(56rem,calc(100dvh-8.5rem))] md:px-8 md:pb-6"
        style={screenBackgroundColor ? { backgroundColor: screenBackgroundColor, backgroundImage: "none" } : undefined}
      >
        {showImage ? (
          <img
            src={imageUrl}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover"
            onError={() => { setHasError(true); }}
          />
        ) : (
          <span
            aria-hidden="true"
            className="relative z-[1] select-none text-xs text-reader-muted opacity-50"
          >
            scene
          </span>
        )}
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-950/10 via-transparent to-slate-950/85"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-slate-950/55 to-transparent"
          aria-hidden="true"
        />
        {hud != null && (
          <div
            data-testid="visual-novel-hud-layer"
            className="absolute right-4 top-4 z-30 md:right-6 md:top-6"
            onClick={(event) => { event.stopPropagation(); }}
          >
            {hud}
          </div>
        )}
        {resolvedMarionActors.length > 0 && (
          <div
            data-testid="visual-novel-marion-layer"
            data-marion-count={resolvedMarionActors.length}
            className={marionLayerBaseClass}
          >
            {resolvedMarionActors.map((actor) => {
              const actorEntryKey = getMarionActorEntryKey(actor);
              const prominence = getMarionActorProminence(actor, hasSpeakingMarionActor);
              return (
                <div
                  key={actorEntryKey}
                  className={getMarionEntryClassName(
                    actor.position,
                    resolvedMarionActors.length,
                    prominence,
                    cutinActor?.characterId != null &&
                      cutinActor.characterId === actor.characterId,
                  )}
                  data-marion-entry={actor.position}
                  data-marion-prominence={prominence}
                  data-marion-cutin-dimmed={
                    cutinActor?.characterId != null &&
                    cutinActor.characterId === actor.characterId
                      ? "true"
                      : "false"
                  }
                  data-marion-entering={
                    enteringMarionActorKeys.has(actorEntryKey) ? "true" : "false"
                  }
                >
                  <div data-marion-entry-motion className="relative h-full w-full">
                    <div
                      className="absolute bottom-0 left-1/2 h-3 w-[72%] -translate-x-1/2 rounded-full bg-black/35 blur-md"
                      aria-hidden="true"
                    />
                    <MarionPlayer
                      manifestUrl={actor.manifestUrl}
                      fallbackImageUrl={actor.fallbackImageUrl}
                      fallbackImageBackupUrl={actor.fallbackImageBackupUrl}
                      hideFallbackImageOnError={actor.exactImageOnly}
                      variantId={actor.variantId}
                      shot={actor.shot ?? "standing"}
                      renderMode={actor.renderMode}
                      name={actor.name}
                      active={prominence !== "dimmed"}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {cutinVisible && cutinActor != null && (
          <div
            data-testid="visual-novel-cutin-backdrop"
            className="pointer-events-none absolute inset-0 z-[15] bg-slate-950/20 backdrop-blur-[2px]"
            aria-hidden="true"
          />
        )}
        {cutinVisible && cutinActor != null && (
          <div
            data-testid="visual-novel-cutin-layer"
            data-cutin-position={cutinActor.position}
            className={cutinLayerClass}
            aria-hidden="true"
          >
            <CutinImage
              actor={cutinActor}
              onImageError={() => setFailedCutinUrl(cutinActor.imageUrl)}
            />
          </div>
        )}
        {shouldRenderChoiceMonologueOverlay && (
          <div
            data-testid="visual-novel-choice-preview-overlay"
            className="pointer-events-none absolute inset-x-0 bottom-4 z-[25] flex justify-center px-5 py-3 text-center text-slate-50 landscape:bottom-6 md:bottom-8 md:px-10 md:py-4"
            aria-hidden="true"
          >
            <ChoiceMonologueFocus
              key={choicePreview?.id}
              testId="visual-novel-choice-preview-focus"
              textTestId="visual-novel-choice-preview-emotion"
              textClassName="text-lg font-semibold leading-snug text-slate-50 drop-shadow-[0_6px_18px_rgba(0,0,0,0.96)] md:text-2xl"
            >
              {choiceMonologue}
            </ChoiceMonologueFocus>
          </div>
        )}
        {shouldRenderChoiceMonologueDock && (
          <div
            data-testid="visual-novel-choice-preview-dock"
            className="pointer-events-none absolute inset-x-4 bottom-24 z-[26] flex min-h-16 items-center justify-center px-5 py-3 text-center text-slate-50 max-md:landscape:bottom-[19%] max-md:landscape:min-h-12 max-md:landscape:py-2 landscape:bottom-[22%] landscape:left-[11%] landscape:right-[12%] md:bottom-[22%] md:left-[11%] md:right-[12%] md:min-h-[4.5rem] md:px-8 md:py-4"
            aria-hidden="true"
          >
            <ChoiceMonologueFocus
              key={choicePreview?.id}
              testId="visual-novel-choice-preview-focus"
              textTestId="visual-novel-choice-preview-emotion"
              textClassName="max-w-[76rem] text-xl font-semibold leading-snug text-slate-50 drop-shadow-[0_6px_20px_rgba(0,0,0,0.96)] md:text-3xl"
            >
              {choiceMonologue}
            </ChoiceMonologueFocus>
          </div>
        )}
        {isComposing && composingMonologue !== null && (
          <div
            data-testid="visual-novel-composing-choice-dock"
            className="pointer-events-none absolute inset-x-4 bottom-24 z-[26] flex justify-center px-5 py-3 text-center text-slate-50 max-md:landscape:bottom-[19%] max-md:landscape:py-2 landscape:bottom-[22%] landscape:left-[11%] landscape:right-[12%] md:bottom-[22%] md:left-[11%] md:right-[12%] md:px-8 md:py-4"
            aria-hidden="true"
          >
            <div className="flex max-w-[76rem] flex-col items-center">
              <ChoiceMonologueFocus
                key={composingPreview?.id}
                testId="visual-novel-composing-choice-focus"
                textTestId="visual-novel-composing-choice-emotion"
                textClassName="text-xl font-semibold leading-snug text-slate-50 drop-shadow-[0_6px_20px_rgba(0,0,0,0.96)] md:text-3xl"
              >
                {composingMonologue}
              </ChoiceMonologueFocus>
            </div>
          </div>
        )}
        {composingStatusRail}
      </div>
      {/* テキスト・選択肢エリア。ステージ下部に重ねて配置し、外側へのはみ出しを防ぐ。 */}
      {textArea}
    </div>
  );
}

function ChoiceMonologueFocus({
  children,
  testId,
  textTestId,
  textClassName,
}: {
  children: ReactNode;
  testId: string;
  textTestId: string;
  textClassName: string;
}): JSX.Element {
  return (
    <span
      data-testid={testId}
      className="choice-monologue-focus relative isolate inline-flex max-w-[76rem] items-center justify-center px-6 py-3 md:px-9 md:py-4"
    >
      <span
        data-testid={`${testId}-scrim`}
        className="absolute inset-x-[-5rem] inset-y-[-1.5rem] -z-20 rounded-[999px] bg-slate-950/[0.72] blur-2xl"
        aria-hidden="true"
      />
      <span
        data-testid={`${testId}-halo`}
        className="choice-monologue-aura absolute inset-x-[-3rem] inset-y-[-1rem] -z-10 rounded-[999px] bg-sky-100/[0.18] blur-2xl"
        aria-hidden="true"
      />
      <span
        data-testid={`${testId}-glass`}
        className="absolute inset-x-[-1.25rem] inset-y-[-0.35rem] -z-10 rounded-[999px] bg-slate-950/[0.36] shadow-[0_0_34px_rgba(226,232,240,0.32),0_22px_48px_rgba(0,0,0,0.58)] backdrop-blur-xl"
        aria-hidden="true"
      />
      <p data-testid={textTestId} className={textClassName}>
        {children}
      </p>
    </span>
  );
}

function getMarionActorEntryKey(actor: VisualNovelMarionActor): string {
  return actor.characterId ?? actor.id;
}

type MarionActorProminence = "normal" | "speaking" | "dimmed";

function getMarionActorProminence(
  actor: VisualNovelMarionActor,
  hasSpeakingActor: boolean,
): MarionActorProminence {
  if (!hasSpeakingActor) return "normal";
  return actor.isSpeaking === true ? "speaking" : "dimmed";
}

function getMarionEntryClassName(
  position: VisualNovelMarionActor["position"],
  actorCount: number,
  prominence: MarionActorProminence,
  dimmedByCutin: boolean,
): string {
  const sizeClass =
    actorCount >= 3
      ? "h-[70%] max-h-[460px] min-w-24 max-w-[32vw] landscape:h-[78%] landscape:max-w-[28vw] md:h-[80%] md:max-h-[520px] md:max-w-[25vw]"
      : "h-[78%] max-h-[500px] min-w-28 max-w-[46vw] landscape:h-[86%] landscape:max-w-[38vw] md:h-[88%] md:max-h-[580px] md:max-w-[34vw]";
  const positionClass =
    position === "left"
      ? "left-4 landscape:left-[12%] md:left-[8%]"
      : position === "center"
        ? "left-1/2 -translate-x-1/2"
        : "right-4 landscape:right-[12%] md:right-[8%]";
  const prominenceClass =
    prominence === "dimmed"
      ? "z-[11] brightness-[0.42]"
      : prominence === "speaking"
        ? "z-[12] brightness-100"
        : "z-[11] brightness-100";
  const cutinClass = dimmedByCutin ? "opacity-20" : "opacity-100";
  return `absolute bottom-0 ${positionClass} ${sizeClass} ${prominenceClass} ${cutinClass} drop-shadow-[0_22px_24px_rgba(0,0,0,0.45)] transition-[filter,opacity] duration-200 ease-out motion-reduce:transition-none`;
}

function getCutinImageClassName(actor: VisualNovelCutinActor): string {
  const url = actor.imageUrl ?? actor.fallbackImageBackupUrl ?? "";
  let pathname = url;
  try {
    pathname = new URL(url, "http://palimia.local").pathname;
  } catch {
    pathname = url;
  }

  // 硝子港のキャラクター立ち絵スプライトをカットイン表示する場合の最適化
  // 頭頂部から胸元・肩・上半身が自然に収まるバストアップ構図に調整
  if (pathname.includes("glass-harbour")) {
    if (/\/(?:rei)(?:[\/._]|$)/.test(pathname)) {
      // レイ: 長身 (頭頂部 y=3.6%, 顔中心 y=13.8%) - 上端余白を確保し肩・胸元まで表示
      return "origin-top scale-[1.0] object-[50%_2%]";
    }
    if (/\/(?:karen)(?:[\/._]|$)/.test(pathname)) {
      // カレン: 標準 (頭頂部 y=8.8%, 顔中心 y=18.5%) - 制服の肩と胸元までしっかり表示
      return "origin-top scale-[1.0] object-[50%_6%]";
    }
    if (/\/(?:mio)(?:[\/._]|$)/.test(pathname)) {
      // ミオ: 小柄 (頭頂部 y=17.8%, 顔中心 y=26.5%) - 顔だけでなくフード・首元・胸元ペンダントまで表示
      return "origin-top scale-[1.04] object-[50%_14%]";
    }
  }

  return "origin-top scale-[1.0] object-[50%_6%]";
}

function CutinImage({
  actor,
  onImageError,
}: {
  actor: VisualNovelCutinActor;
  onImageError: () => void;
}): JSX.Element {
  const imageClassName = getCutinImageClassName(actor);

  return (
    <div
      key={actor.id}
      data-testid="visual-novel-cutin-frame"
      className="h-[min(56vh,26rem)] w-[min(72vw,26rem)] overflow-hidden drop-shadow-[0_28px_34px_rgba(0,0,0,0.6)] landscape:h-[min(72vh,29rem)] landscape:w-[min(36vw,26rem)] md:h-[min(65vh,30rem)] md:w-[min(32vw,28rem)]"
      style={{
        WebkitMaskImage: "linear-gradient(to bottom, black 82%, transparent 100%)",
        maskImage: "linear-gradient(to bottom, black 82%, transparent 100%)",
      }}
    >
      <MarionPlayer
        manifestUrl={actor.manifestUrl}
        fallbackImageUrl={actor.imageUrl}
        fallbackImageBackupUrl={actor.fallbackImageBackupUrl}
        variantId={actor.variantId}
        shot="cutin"
        fit="cover"
        layout="frame"
        imageTestId="visual-novel-cutin-image"
        imageClassName={imageClassName}
        hideFallbackImageOnError={actor.exactImageOnly}
        onFallbackImageError={onImageError}
        name={actor.name}
      />
    </div>
  );
}
